import Accordion from '@base/components/src/Accordion';
import AnchorDialog from '@base/components/src/AnchorDialog';
import {
  CustomConfigurationElement,
  ProductAddonElement,
  ProductCustomConfiguration,
} from '@package/core/src/shoppingCart';
import useProductAddons from '@package/hooks/src/useProductAddons';
import clsx from 'clsx';
import { Fragment, memo } from 'react';

import MissingItemsModal from '../MissingItemModal';
import ProductServiceContent from '../ProductServiceContent';
import config from 'config-base';
import Addon from './Addon';
import AddonCard from './AddonCard';
import { AddonsProps } from './types';
import styles from './CartProductAddons.module.css';

export function CartProductAddonsComponent({
  productSku,
  productQuantity,
  addProduct,
  addProductService,
  deleteProductService,
  relatedProductsAvailable,
}: AddonsProps) {
  const { productAddons } = useProductAddons(productSku, relatedProductsAvailable) as any;
  const isProductAddons =
    productAddons?.customConfigurations?.length > 0 ||
    productAddons?.serviceContracts?.length > 0 ||
    productAddons?.accessoryList?.length > 0 ||
    productAddons?.ammoList?.length > 0;
  return relatedProductsAvailable ? (
    <>
      {productAddons && isProductAddons && (
        <Accordion
          title={'Services & Add-ons'}
          className={clsx(styles.addon, { [styles.addonAGD]: config.brandCode !== '0' })}
          headerClassName={styles.accordironAddonsHeader}
          titleClass={styles.accordionAddonsTitle}
          iconClassName={styles.accordionIcon}
          contentClassName={styles.accordionContent}
          data-testid="product-addons"
        >
          {productAddons && (
            <div className={styles.addonsList}>
              {productAddons?.serviceContracts?.map((addon: any) => (
                <ProductServiceContent
                  key={`${addon.seq}${productQuantity}`}
                  className={styles.service}
                  title={addon.name}
                  productSku={productSku}
                  services={addon.elements}
                  addProductService={async (service) => {
                    await addProductService({
                      serviceId: service.id,
                      productSku,
                      productQuantity,
                      serviceType: addon.name,
                      product: service,
                    });
                  }}
                  deleteProductService={async () => await deleteProductService(addon.name)}
                />
              ))}
              {productAddons.customConfigurations.length > 0 &&
                productAddons.customConfigurations.map((configuration: ProductCustomConfiguration, idx: number) => (
                  <Addon<CustomConfigurationElement>
                    key={idx}
                    title={configuration.name}
                    data={configuration.elements}
                    renderAddonItem={(product) => (
                      <AnchorDialog
                        title={'Service Plan'}
                        contentClassName={styles.serviceModal}
                        anchorContent={(open: (() => void) | undefined) => (
                          <AddonCard
                            data={product}
                            outOfStock={!product.canAddToCart}
                            onClick={product.canAddToCart ? open : undefined}
                            onAddProduct={
                              product.canAddToCart
                                ? async () => {
                                    await addProductService({
                                      serviceId: product.id,
                                      productSku,
                                      productQuantity,
                                      serviceType: configuration.name,
                                      product,
                                    });
                                  }
                                : async () => open && open()
                            }
                          />
                        )}
                      >
                        {(_: any, close: () => void) => (
                          <MissingItemsModal
                            infoMode={product.canAddToCart}
                            addService={async () => {
                              await addProductService({
                                serviceId: product.id,
                                productSku,
                                productQuantity,
                                serviceType: configuration.name,
                                product,
                              });
                            }}
                            productSku={productSku}
                            serviceId={product.id}
                            closeModal={close}
                          />
                        )}
                      </AnchorDialog>
                    )}
                  />
                ))}
              {productAddons.accessoryList.length > 0 && (
                <Addon<ProductAddonElement>
                  id="accesories-list"
                  title={'Accessories'}
                  data={productAddons.accessoryList}
                  renderAddonItem={(product) => (
                    <AddonCard
                      data={product}
                      onAddProduct={async () => await addProduct([{ productSKU: product.sku, product }])}
                    />
                  )}
                />
              )}
              {productAddons.ammoList.length > 0 && (
                <Addon<ProductAddonElement>
                  id="ammo-list"
                  title={'Ammo'}
                  data={productAddons.ammoList}
                  renderAddonItem={(product) => (
                    <AddonCard
                      data={product}
                      onAddProduct={async () => await addProduct([{ productSKU: product.sku, product }])}
                    />
                  )}
                />
              )}
            </div>
          )}
        </Accordion>
      )}
      {productAddons?.scopeAccessories?.length > 0 && (
        <Accordion
          title={'Mounts'}
          className={clsx(styles.addon, {
            [styles.addonAGD]: config.brandCode !== '0',
          })}
          headerClassName={styles.accordironAddonsHeader}
          titleClass={styles.accordionAddonsTitle}
          iconClassName={styles.accordionIcon}
          contentClassName={styles.accordionContent}
          data-testid="product-mounts"
        >
          {productAddons?.scopeAccessories.map((scopeAccessorieItems: ProductAddonElement, index: number) => {
            const accesorieKeys = Object.keys(scopeAccessorieItems);
            return (
              <Fragment key={index}>
                {accesorieKeys.map((scopeTitle: string) => (
                  <Fragment key={scopeTitle}>
                    <Addon<ProductAddonElement>
                      id="mounts-list"
                      title={scopeTitle}
                      data={scopeAccessorieItems[scopeTitle]}
                      renderAddonItem={(product) => (
                        <AddonCard
                          data={product}
                          onAddProduct={async () => await addProduct([{ productSKU: product.sku, product }])}
                        />
                      )}
                    />
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
        </Accordion>
      )}
    </>
  ) : null;
}

const CartProductAddons = memo(CartProductAddonsComponent);

export default CartProductAddons;
